/**
 * AI Elements 适配器函数
 *
 * 提供内部 ChatMessage 格式与 AI Elements 格式之间的双向转换，
 * 以实现无缝集成。
 *
 * v2.0: 从 ChatUI 迁移到 AI Elements
 * v3.0: 支持 parts-based 消息模型
 *
 * 注意：所有用户界面文字、提示信息、状态文字必须使用中文
 */

import type {
  ChatMessage,
  ToolCall,
  MessagePart,
  StreamingMessagePart,
} from '../types/chat';

/**
 * Skill activation data for AI Elements message content
 */
export interface SkillActivationData {
  skillName: string;
  description?: string;
  status: 'activating' | 'active' | 'error';
  error?: string;
  timestamp: string;
}

/**
 * Subagent invocation data for AI Elements message content
 */
export interface SubagentInvocationData {
  subagentName: string;
  task?: string;
  status: 'invoking' | 'working' | 'completed' | 'error';
  result?: string;
  error?: string;
  executionTime?: number;
  timestamp: string;
  streamContent?: string;
  toolCalls?: Array<{
    tool_call_id?: string;
    tool_name: string;
    status: 'detected' | 'executing' | 'success' | 'failed';
    tool_args?: Record<string, unknown>;
    result?: unknown;
    error?: string;
    timestamp: string;
  }>;
  messages?: Array<{
    role: 'assistant' | 'tool';
    content?: string;
    tool_name?: string;
    timestamp: string;
  }>;
}

/**
 * Tool call data for AI Elements
 */
export interface ToolCallData {
  toolCallId?: string;
  toolName: string;
  status: 'detected' | 'executing' | 'success' | 'failed';
  args?: Record<string, unknown>;
  result?: unknown;
  error?: string;
  timestamp: string;
}

/**
 * Tool part for AI Elements
 */
export interface AIElementsToolPart {
  type: 'tool';
  toolCallId: string;
  toolName: string;
  status: 'detected' | 'executing' | 'success' | 'failed';
  args?: Record<string, unknown>;
  result?: unknown;
  error?: string;
  timestamp: string;
}

/**
 * Reasoning part for AI Elements
 */
export interface AIElementsReasoningPart {
  type: 'reasoning';
  content: string;
  isDone: boolean;
  reasoningTime?: number;
}

/**
 * Text part for AI Elements
 */
export interface AIElementsTextPart {
  type: 'text';
  content: string;
}

/**
 * Message parts union type
 */
export type AIElementsMessagePart =
  | AIElementsTextPart
  | AIElementsReasoningPart
  | AIElementsToolPart;

/**
 * AI Elements Message Props
 */
export interface AIElementsMessageProps {
  id: string;
  type: 'text' | 'image' | 'system' | 'tool-calls' | 'thinking' | 'skill-activation' | 'subagent-invocation';
  role: 'user' | 'assistant' | 'system';
  content: string;
  parts?: AIElementsMessagePart[];
  reasoning?: AIElementsReasoningPart;
  toolCalls?: ToolCallData[];
  skillActivation?: SkillActivationData;
  subagentInvocation?: SubagentInvocationData;
  imageUrl?: string;
  createdAt: number;
  hasTime?: boolean;
  avatar?: string;
}

// ============== Helper Functions ==============

function generateMessageId(timestamp: string, index: number): string {
  return `msg-${timestamp}-${index}`;
}

function safeParseJson(jsonString: string): Record<string, unknown> | undefined {
  try {
    return JSON.parse(jsonString);
  } catch {
    return undefined;
  }
}

const IMAGE_REFERENCE_PATTERN = /!\[image_upload\]\{([^}]+)\}/;

export function mapToolStatus(
  internalStatus: 'detected' | 'executing' | 'success' | 'failed'
): 'detected' | 'input-streaming' | 'input-available' | 'output-streaming' | 'output-available' | 'output-error' | 'loading' {
  const statusMap: Record<
    'detected' | 'executing' | 'success' | 'failed',
    'detected' | 'input-streaming' | 'input-available' | 'output-streaming' | 'output-available' | 'output-error' | 'loading'
  > = {
    detected: 'input-streaming',
    executing: 'input-available',
    success: 'output-available',
    failed: 'output-error',
  };
  return statusMap[internalStatus];
}

function determineMessageType(message: ChatMessage): AIElementsMessageProps['type'] {
  if (message.skill_activation) return 'skill-activation';
  if (message.subagent_invocation) return 'subagent-invocation';
  if (message.tool_calls && message.tool_calls.length > 0) return 'tool-calls';
  if (message.thinking_process) return 'thinking';
  const imageMatches = message.content.match(IMAGE_REFERENCE_PATTERN);
  if (imageMatches && message.content.replace(IMAGE_REFERENCE_PATTERN, '').trim() === '') {
    return 'image';
  }
  return 'text';
}

function convertToolCalls(toolCalls: ToolCall[]): ToolCallData[] {
  return toolCalls.map((tc) => {
    if ('tool_call_id' in tc && tc.tool_call_id) {
      return {
        toolCallId: tc.tool_call_id,
        toolName: tc.tool_name,
        status: tc.status,
        args: tc.tool_args,
        result: tc.result,
        error: tc.error,
        timestamp: tc.timestamp,
      };
    }
    const legacyTc = tc as unknown as Record<string, unknown>;
    if (legacyTc.function && (legacyTc.function as Record<string, unknown>).name) {
      return {
        toolCallId: (legacyTc.id as string) || `legacy_${(legacyTc.function as Record<string, unknown>).name}`,
        toolName: (legacyTc.function as Record<string, unknown>).name as string,
        status: (legacyTc.status as ToolCallData['status']) || 'success',
        args: (legacyTc.function as Record<string, unknown>).arguments
          ? safeParseJson((legacyTc.function as Record<string, unknown>).arguments as string)
          : undefined,
        result: legacyTc.result,
        error: legacyTc.error as string | undefined,
        timestamp: (legacyTc.timestamp as string) || new Date().toISOString(),
      };
    }
    return {
      toolCallId: (legacyTc.id as string) || (legacyTc.tool_call_id as string),
      toolName: (legacyTc.tool_name as string) || (legacyTc.name as string) || 'unknown',
      status: tc.status,
      args: (legacyTc.tool_args as Record<string, unknown>) || (legacyTc.args as Record<string, unknown>),
      result: tc.result,
      error: tc.error,
      timestamp: tc.timestamp,
    };
  });
}

// ============== Parts Expansion (Task 8.1) ==============

export interface PartsToAIElementsOptions {
  baseMessageId: string;
  role: 'user' | 'assistant' | 'system';
  createdAt: number;
  sessionId?: string;
  avatar?: string;
}

export function partsToAIElements(
  parts: MessagePart[],
  options: PartsToAIElementsOptions
): AIElementsMessageProps[] {
  const result: AIElementsMessageProps[] = [];
  const { baseMessageId, role, createdAt, avatar } = options;

  const sortedParts = [...parts].sort((a, b) => a.sequence - b.sequence);

  let currentGroup: AIElementsMessagePart[] = [];
  let groupStartIdx = 0;

  const flushGroup = () => {
    if (currentGroup.length === 0) return;
    result.push({
      id: `${baseMessageId}-text-${groupStartIdx}`,
      type: 'text',
      role,
      content: '',
      parts: [...currentGroup],
      createdAt,
      hasTime: groupStartIdx === 0,
      avatar,
    });
    currentGroup = [];
  };

  for (let partIdx = 0; partIdx < sortedParts.length; partIdx++) {
    const part = sortedParts[partIdx];

    if (part.type === 'text') {
      if (currentGroup.length === 0) groupStartIdx = partIdx;
      currentGroup.push({ type: 'text', content: part.content || '' });
    } else if (part.type === 'reasoning') {
      if (currentGroup.length === 0) groupStartIdx = partIdx;
      currentGroup.push({
        type: 'reasoning',
        content: part.content || '',
        isDone: true,
      });
    } else if (part.type === 'tool_call') {
      flushGroup();

      const toolCallData: ToolCallData = {
        toolCallId: part.tool_call_id || '',
        toolName: part.tool_name || '',
        status: part.status || 'detected',
        args: part.tool_args,
        result: part.result,
        error: part.error,
        timestamp: createdAt.toString(),
      };

      let subagentInvocation: SubagentInvocationData | undefined;
      if (part.subagent_invocation) {
        subagentInvocation = {
          subagentName: part.subagent_invocation.subagent_name,
          task: part.subagent_invocation.task,
          status: part.subagent_invocation.status,
          result: part.subagent_invocation.result,
          error: part.subagent_invocation.error,
          executionTime: part.subagent_invocation.execution_time,
          timestamp: createdAt.toString(),
        };
      }

      if (subagentInvocation) {
        result.push({
          id: `${baseMessageId}-subagent-${partIdx}`,
          type: 'subagent-invocation',
          role,
          content: '',
          subagentInvocation,
          createdAt,
          hasTime: false,
          avatar,
        });
      } else {
        result.push({
          id: `${baseMessageId}-tool-${partIdx}`,
          type: 'tool-calls',
          role,
          content: '',
          toolCalls: [toolCallData],
          createdAt,
          hasTime: false,
          avatar,
        });
      }
    }
  }

  flushGroup();

  return result;
}

// ============== Streaming Parts Conversion (Task 8.3) ==============

export interface StreamingPartsToAIElementsOptions {
  parts: StreamingMessagePart[];
  isComplete: boolean;
  sessionId?: string;
  streamingStartTime?: number;
}

export function streamingPartsToAIElements(
  options: StreamingPartsToAIElementsOptions
): AIElementsMessageProps[] {
  const { parts, isComplete, sessionId, streamingStartTime } = options;
  const now = Date.now();
  const avatarSeed = sessionId || 'assistant';
  const avatar = `https://api.dicebear.com/7.x/bottts/svg?seed=${avatarSeed}`;

  const sortedParts = [...parts].sort((a, b) => a.sequence - b.sequence);

  const result: AIElementsMessageProps[] = [];
  let currentGroup: AIElementsMessagePart[] = [];
  let groupStartIdx = 0;

  const flushGroup = () => {
    if (currentGroup.length === 0) return;
    result.push({
      id: `streaming-text-${groupStartIdx}`,
      type: 'text',
      role: 'assistant',
      content: '',
      parts: [...currentGroup],
      createdAt: now,
      hasTime: groupStartIdx === 0,
      avatar,
    });
    currentGroup = [];
  };

  for (let i = 0; i < sortedParts.length; i++) {
    const part = sortedParts[i];

    if (part.type === 'text') {
      if (currentGroup.length === 0) groupStartIdx = i;
      currentGroup.push({ type: 'text', content: part.content || '' });
    } else if (part.type === 'reasoning') {
      if (currentGroup.length === 0) groupStartIdx = i;
      currentGroup.push({
        type: 'reasoning',
        content: part.content || '',
        isDone: isComplete,
        reasoningTime: streamingStartTime
          ? Math.round((now - streamingStartTime) / 1000)
          : undefined,
      });
    } else if (part.type === 'tool') {
      flushGroup();

      const toolCallData: ToolCallData = {
        toolCallId: part.tool_call_id || '',
        toolName: part.tool_name || '',
        status: part.status || 'detected',
        args: part.tool_args,
        result: part.result,
        error: part.error,
        timestamp: now.toString(),
      };

      result.push({
        id: `streaming-tool-${i}`,
        type: 'tool-calls',
        role: 'assistant',
        content: '',
        toolCalls: [toolCallData],
        createdAt: now,
        hasTime: false,
        avatar,
      });
    }
  }

  flushGroup();

  return result;
}

// ============== AI Elements Adapter Functions ==============

export function toAIElementsMessage(
  message: ChatMessage,
  index: number,
  sessionId?: string,
  _currentAgentName?: string
): AIElementsMessageProps {
  try {
    if (!message) {
      console.warn('[chatAdapter] Received null/undefined message at index', index);
      return createErrorMessage('Invalid message data', index);
    }

    const messageId = generateMessageId(message.timestamp || new Date().toISOString(), index);
    const role = message.role as 'user' | 'assistant' | 'system';
    const createdAt = message.timestamp ? new Date(message.timestamp).getTime() : Date.now();
    const messageType = determineMessageType(message);
    const avatarSeed = sessionId || 'assistant';
    const avatar =
      role === 'user'
        ? undefined
        : `https://api.dicebear.com/7.x/bottts/svg?seed=${avatarSeed}`;

    const result: AIElementsMessageProps = {
      id: messageId,
      type: messageType,
      role,
      content: '',
      createdAt,
      hasTime: true,
      avatar,
    };

    if (messageType === 'skill-activation' && message.skill_activation) {
      result.skillActivation = {
        skillName: message.skill_activation.skill_name,
        description: message.skill_activation.description,
        status: message.skill_activation.status,
        error: message.skill_activation.error,
        timestamp: message.timestamp,
      };
      result.content = message.content;
      if (message.thinking_process) {
        result.reasoning = {
          content: message.thinking_process,
          isDone: true,
          type: 'reasoning',
        };
      }
      return result;
    }

    if (messageType === 'subagent-invocation' && message.subagent_invocation) {
      result.subagentInvocation = {
        subagentName: message.subagent_invocation.subagent_name,
        task: message.subagent_invocation.task,
        status: message.subagent_invocation.status,
        result: message.subagent_invocation.result,
        error: message.subagent_invocation.error,
        executionTime: message.subagent_invocation.execution_time,
        timestamp: message.timestamp,
      };
      result.content = message.content;
      if (message.thinking_process) {
        result.reasoning = {
          content: message.thinking_process,
          isDone: true,
          type: 'reasoning',
        };
      }
      return result;
    }

    if (messageType === 'image') {
      const imageMatch = message.content.match(IMAGE_REFERENCE_PATTERN);
      if (imageMatch) {
        result.imageUrl = `/api/chat/images/${imageMatch[1]}`;
      }
      if (message.thinking_process) {
        result.reasoning = {
          content: message.thinking_process,
          isDone: true,
          type: 'reasoning',
        };
      }
      return result;
    }

    result.content = message.content.replace(IMAGE_REFERENCE_PATTERN, '').trim();

    if (messageType === 'tool-calls' && message.tool_calls) {
      result.toolCalls = convertToolCalls(message.tool_calls);
    }

    if (message.thinking_process) {
      result.reasoning = {
        content: message.thinking_process,
        isDone: true,
        type: 'reasoning',
      };
    }

    return result;
  } catch (error) {
    console.error('[chatAdapter] Error converting message:', error);
    return createErrorMessage('消息处理错误', index);
  }
}

function createErrorMessage(errorText: string, index: number): AIElementsMessageProps {
  return {
    id: `error-${index}`,
    type: 'system',
    role: 'system',
    content: `⚠️ ${errorText}`,
    createdAt: Date.now(),
    hasTime: false,
  };
}

export function toAIElementsMessages(
  messages: ChatMessage[],
  sessionId?: string,
  currentAgentName?: string
): AIElementsMessageProps[] {
  const result: AIElementsMessageProps[] = [];
  let globalIndex = 0;

  for (const message of messages) {
    if (message.parts && message.parts.length > 0) {
      const baseMessageId = generateMessageId(
        message.timestamp || new Date().toISOString(),
        globalIndex
      );
      const role = message.role as 'user' | 'assistant' | 'system';
      const createdAt = message.timestamp
        ? new Date(message.timestamp).getTime()
        : Date.now();
      const avatarSeed = sessionId || 'assistant';
      const avatar =
        role === 'user'
          ? undefined
          : `https://api.dicebear.com/7.x/bottts/svg?seed=${avatarSeed}`;

      const expandedMessages = partsToAIElements(message.parts, {
        baseMessageId,
        role,
        createdAt,
        sessionId,
        avatar,
      });

      result.push(...expandedMessages);
    } else {
      result.push(toAIElementsMessage(message, globalIndex, sessionId, currentAgentName));
    }
    globalIndex++;
  }

  return result;
}

export function toStreamingAIElementsMessage(
  content: string,
  thinkingProcess?: string | null,
  isThinking?: boolean,
  _currentAgentName?: string,
  sessionId?: string,
  parts?: StreamingMessagePart[]
): AIElementsMessageProps {
  const now = Date.now();
  const avatarSeed = sessionId || 'assistant';
  const avatar = `https://api.dicebear.com/7.x/bottts/svg?seed=${avatarSeed}`;

  if (parts && parts.length > 0) {
    const expandedMessages = streamingPartsToAIElements({
      parts,
      isComplete: !isThinking,
      sessionId,
      streamingStartTime: now,
    });
    if (expandedMessages.length > 0) {
      return expandedMessages[expandedMessages.length - 1];
    }
  }

  const result: AIElementsMessageProps = {
    id: 'streaming',
    type: thinkingProcess ? 'thinking' : 'text',
    role: 'assistant',
    content: content,
    createdAt: now,
    hasTime: true,
    avatar,
  };

  if (thinkingProcess) {
    result.reasoning = {
      type: 'reasoning',
      content: thinkingProcess,
      isDone: !isThinking,
    };
  }

  return result;
}

export function toToolCallData(toolCall: ToolCall): ToolCallData {
  if ('tool_call_id' in toolCall && toolCall.tool_call_id) {
    return {
      toolCallId: toolCall.tool_call_id,
      toolName: toolCall.tool_name,
      status: toolCall.status,
      args: toolCall.tool_args,
      result: toolCall.result,
      error: toolCall.error,
      timestamp: toolCall.timestamp,
    };
  }

  const legacyTc = toolCall as unknown as Record<string, unknown>;
  return {
    toolCallId: (legacyTc.id as string) || `legacy_${legacyTc.tool_name}`,
    toolName: (legacyTc.tool_name as string) || ((legacyTc.function as Record<string, unknown>)?.name as string) || 'unknown',
    status: legacyTc.status as ToolCallData['status'],
    args:
      (legacyTc.tool_args as Record<string, unknown>) ||
      ((legacyTc.function as Record<string, unknown>)?.arguments
        ? safeParseJson((legacyTc.function as Record<string, unknown>).arguments as string)
        : undefined),
    result: legacyTc.result,
    error: legacyTc.error as string | undefined,
    timestamp: legacyTc.timestamp as string,
  };
}

export function toToolCallDataArray(toolCalls: ToolCall[]): ToolCallData[] {
  return toolCalls.map(toToolCallData);
}

// ============== System Message Helpers ==============

export function createSystemMessage(text: string): AIElementsMessageProps {
  return {
    id: `system-${Date.now()}`,
    type: 'system',
    role: 'system',
    content: text,
    createdAt: Date.now(),
    hasTime: false,
  };
}

export function createSessionStartMessage(sessionTitle?: string): AIElementsMessageProps {
  const title = sessionTitle || '新对话';
  return createSystemMessage(`会话已开始: ${title}`);
}

export function createSessionEndMessage(
  sessionTitle?: string,
  messageCount?: number
): AIElementsMessageProps {
  const title = sessionTitle || '对话';
  const count = messageCount ? `(${messageCount} 条消息)` : '';
  return createSystemMessage(`会话已结束: ${title} ${count}`);
}

export function createUserMessage(content: string): AIElementsMessageProps {
  return {
    id: `user-${Date.now()}`,
    type: 'text',
    role: 'user',
    content: content,
    createdAt: Date.now(),
    hasTime: true,
  };
}

export const chatAdapter = {
  toAIElementsMessage,
  toAIElementsMessages,
  toStreamingAIElementsMessage,
  partsToAIElements,
  streamingPartsToAIElements,
  toToolCallData,
  toToolCallDataArray,
  createSystemMessage,
  createUserMessage,
  mapToolStatus,
};

export default chatAdapter;